import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from 'styled-components'

import { AvatarLetters, InputWithLabel, Loading, Text, ToastContext, UploadImage } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import AppContext from '@/common/context/app.context'

import GroupChips from './GroupChips/GroupChips'
import useUserInfo from './hooks/useUserInfo'
import { getPayload } from './helpers'
import * as Styles from './styles'

const UserInfo = ({ user, groups, isOwner: isMerchantOwner, storeId }) => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const { setToastInfo } = useContext(ToastContext)
  const { info, updateContext } = useContext(AppContext)

  const {
    image,
    name,
    setName,
    lastName,
    setLastName,
    username,
    setUsername,
    email,
    changeImage,
    deleteImage,
    editUserProfile,
    applyingUserProfile,
    hasChanges,
    setHasChanges,
    revertChanges,
  } = useUserInfo(user)

  const onSave = async () => {
    const payload = getPayload({ name, lastName, username, email, image })

    try {
      const data = await editUserProfile({ storeId: storeId, userId: user.id, user: payload })

      updateContext({
        ...info,
        userInfo: data,
      })

      setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
      setHasChanges(false)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return (
    <Loading show={applyingUserProfile}>
      <Styles.Container>
        {hasChanges && (
          <Styles.WarningContainer>
            <UnsavedChangesAlert />
          </Styles.WarningContainer>
        )}
        <Styles.UserImageContainer>
          {image?.preview ? (
            <img src={image?.preview} alt={name} />
          ) : (
            <AvatarLetters id={user?.id} firstName={user?.first_name} lastName={user?.last_name} />
          )}
          <UploadImage
            label={t('Imagen de perfil')}
            recommendation={t('Max 1MB. Formatos: JPG, JPEG, PNG, HEIC.')}
            onChange={file => changeImage(file)}
            onDelete={() => deleteImage()}
            preview={image?.preview}
          />
        </Styles.UserImageContainer>
        <Styles.UserInfoContainer>
          <Text type="titleS" color={theme.colors.text.default}>
            {t('Detalles de tu cuenta')}
          </Text>
          <Styles.InputsContainer>
            <InputWithLabel label={t('Nombre')} value={name} onChange={e => setName(e.target.value)} fullWidth />
            <InputWithLabel
              label={t('Apellidos')}
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              fullWidth
            />
          </Styles.InputsContainer>
          <Styles.InputsContainer>
            <InputWithLabel label={t('Alias')} value={username} onChange={e => setUsername(e.target.value)} fullWidth />
            <InputWithLabel
              label={t('Correo electrónico')}
              value={email}
              fullWidth
              disabled
              helperText={t('No se puede cambiar el email de un usuario registrado')}
            />
          </Styles.InputsContainer>
          <Styles.GroupsContainer>
            <Text type="bodyMRegular" color={theme.colors.text.subtle}>
              {t('Grupos')}
            </Text>
            <GroupChips groups={groups} isOwner={isMerchantOwner} />
            <Text type="bodyMRegular" color={theme.colors.text.subtle}>
              {t('Fecha de registro')}
            </Text>
            <Text type="bodyMRegular">
              {new Date(user?.created).toLocaleDateString(i18n.language, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </Text>
          </Styles.GroupsContainer>
        </Styles.UserInfoContainer>
        <SaveChangesButtons
          hasChanges={hasChanges}
          onSave={onSave}
          onRevertChanges={revertChanges}
          loading={applyingUserProfile}
          disabled={applyingUserProfile}
        />
      </Styles.Container>
    </Loading>
  )
}

export default UserInfo
